import {useRef, useEffect} from 'react';
import {useLocation, useSearchParams, useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import _ from 'lodash';
import {useEngagement, useQuery, useDocumentId, useActiveFrame} from '../customHooks';
import {getAccountById, getEstimateById, getTaskDocumentByDocumentId, getSTEntityById, getTaskDocumentsByIdSlice} from '../../selectors/Ares/selectors';
import Utility from '../../util/utilityfunctions';
import {isNullOrUndefined, sortRisks, labels} from '../../util/utils';
import {getFormData} from '../../actions/FormData/formdataactions';
import {getMateriality as _getMateriality} from '../../actions/Materiality/materialityactions';
import {matchRoutes} from 'react-router';
import queryString from 'query-string';
import Env from '../../util/env';
import {
	documentSearchTypes,
	documentCategoryNames,
	serviceURL,
	positiveNumberWithOneDecimalRegex,
	numberFormats,
	IndependenceAndProfileDocumentTypes,
	documentTypesShowedOnLeftNav,
	TaskTypeEnum,
	riskTypes,
	clientSideEventNames,
	documentTypes
} from '../uiconstants';
import {createEntityForm} from '../../actions/EntityForms/entityformsactions';
import {getAllAresDocuments} from '../../actions/Document/documentactions';
import {useCallback} from 'react';
import {RiskFilters} from './Filters/riskFilters';
import {showBlockingLoader} from '../../components/Common/BlockingLoadingIndicator/BlockingLoadingIndicator';

export function useAresUrlParameters() {
	const pathName = useLocation().pathname;

	const match = matchRoutes(
		[
			{path: '/guidedworkflowv2/minimal/:documentId/header/:headerId/section/:sectionId?'},
			{path: '/guidedworkflowv2/minimal/:documentId?'},
			{path: '/guidedworkflow/:documentId/header/:headerId/section/:sectionId?'},
			{path: '/guidedworkflow/:documentId/header/:headerId'},
			{path: '/guidedworkflow/:documentId/summary'},
			{path: '/guidedworkflow/:documentId'},
			{path: '/guidedworkflow'},
			{path: '/guidedworkflowv2/:documentId/header/:headerId/section/:sectionId?'},
			{path: '/guidedworkflowv2/:documentId/header/:headerId'},
			{path: '/guidedworkflowv2/:documentId/summary'},
			{path: '/guidedworkflowv2/:documentId'},
			{path: '/guidedworkflowv2/changes/:documentId?'},
			{path: '/guidedworkflowv2/compare/:documentId?'},
			{path: '/guidedworkflowv2/compare/:documentId/header/:headerId/section/:sectionId?'},
			{path: '/guidedworkflowv2/attributes/:documentId?'},
			{path: '/guidedworkflowv2/relateddocuments/:documentId?'},
			{path: '/guidedworkflowv2'}
		],
		pathName
	);

	const queryStringParameters = useQuery();

	const urlParameters = {
		taskId: queryStringParameters.taskid,
		engagementId: queryStringParameters.engagementid !== undefined ? queryStringParameters.engagementid : undefined,
		documentId: queryStringParameters.documentid !== undefined ? queryStringParameters.documentid : undefined,
		headerId: undefined,
		sectionId: undefined,
		stEntityId: undefined
	};

	if (Array.isArray(match) && match.length > 0 && match[0].params) {
		const url = match[0].pathname;
		const {documentId, headerId, sectionId} = match[0].params;

		urlParameters.documentId = documentId?.toLowerCase();
		urlParameters.headerId = headerId !== undefined ? Number(headerId) : undefined;
		urlParameters.sectionId = sectionId !== undefined ? Number(sectionId) : undefined;
		urlParameters.isSummary = url.toLowerCase().includes('summary');
		urlParameters.isBodyDetailView = queryStringParameters?.isbodydetailview ? true : false;
		urlParameters.stEntityId = queryStringParameters.stentityid !== undefined ? Number(queryStringParameters.stentityid) : undefined;
		urlParameters.summaryRedirect = Boolean(queryStringParameters?.summarysection?.toLowerCase?.() === 'true');
	}

	return urlParameters;
}

export function useAresUrlTrackChangesParameters() {
	const match = matchRoutes([{path: '/guidedworkflowv2/changes/:documentId'}], useLocation().pathname);

	const queryStringParameters = useQuery();

	const urlParameters = {
		taskId: queryStringParameters.taskid,
		documentId: undefined,
		snapshotId: queryStringParameters.snapshotid !== undefined ? Number(queryStringParameters.snapshotid) : undefined,
		isTrackChanges: queryStringParameters.snapshotid !== undefined ? Boolean(queryStringParameters.istrackchanges.toLowerCase() === 'true') : undefined
	};

	if (Array.isArray(match) && match.length > 0 && match[0].params) {
		const {documentId} = match[0].params;
		urlParameters.documentId = documentId?.toLowerCase();
	}

	return urlParameters;
}

export function useAresUrlCompareResponsesParameters() {
	const match = matchRoutes(
		[
			{path: '/guidedworkflowv2/minimal/:documentId/header/:headerId/section/:sectionId?'},
			{path: '/guidedworkflowv2/minimal/:documentId?'},
			{path: '/guidedworkflowv2/compare/:documentId'},
			{path: '/guidedworkflowv2/compare/:documentId/header/:headerId/section/:sectionId?'}
		],
		useLocation().pathname
	);

	const queryStringParameters = useQuery();

	const urlParameters = {
		taskId: queryStringParameters.taskid,
		documentId: undefined,
		isCompareResponses: match && match[0]?.pathname.includes('compare') ? true : queryStringParameters?.iscompareresponses ? true : false,
		isBodyDetailView: queryStringParameters?.isbodydetailview ? true : false
	};

	if (Array.isArray(match) && match.length > 0 && match[0].params) {
		const {documentId} = match[0].params;
		urlParameters.documentId = documentId?.toLowerCase();
	}

	return urlParameters;
}

/**
 * Custom hook that returns a boolean <b>value</b> indicating whether the user is in Ares (FIT) or not.
 * @returns {bool} true if the user is in Ares (FIT); false otherwise.
 */
export function useIsAresView() {
	const isAresview = useSelector((state) => state.isAresView);
	return isAresview;
}

export function useIsIndependenceOrProfile(docId = null) {
	const docType = useDocumentTypeId(docId);
	return IndependenceAndProfileDocumentTypes.includes(docType);
}

/**
 * Custom hook that returns a boolean <b>value</b> indicating whether the user is viewing a form inside a drawer or not.
 * @returns {bool} true if the form is viewed in "drawer mode"; false otherwise.
 */
export function useIsDrawerMode() {
	const isDrawer = useSelector((state) => state.isDrawerMode);
	return isDrawer;
}

/**
 * Custom hook that returns a boolean <b>value</b> indicating whether the user is viewing a form as track changes view or not.
 * @returns {bool} true if the form is viewed in "track changes view"; false otherwise.
 */
export function useIsTrackChangesView() {
	const {isTrackChanges} = useAresUrlTrackChangesParameters();

	const isTrackChangesView = useSelector((state) => state.isTrackChangesView);

	return isTrackChanges || isTrackChangesView;
}

/**
 * Custom hook that returns a boolean <b>value</b> indicating whether the user is viewing a form as compare responses view or not.
 * @returns {bool} true if the form is viewed in "compare response view"; false otherwise.
 */
export function useIsCompareResponsesView() {
	const {isCompareResponses} = useAresUrlCompareResponsesParameters();

	const isCompareResponsesView = useSelector((state) => state.isCompareResponsesView);

	return isCompareResponses || isCompareResponsesView;
}

/**
 * Custom hook that returns a boolean <b>value</b> indicating whether the user is viewing a form inside a drawer or not.
 * @returns {bool} true if the form is viewed in "drawer mode"; false otherwise.
 */
export function useIsGuidedWorkFlowV2() {
	const isGuidedWorkFlowV2 = useSelector((state) => state.isGuidedWorkFlowV2);
	return isGuidedWorkFlowV2;
}

export function useDocumentTypeId(docId = null) {
	const document = useSelector((store) => {
		if (docId) {
			return getTaskDocumentByDocumentId(store, docId);
		}
		return getTaskDocumentByDocumentId(store, store.documentId);
	});

	return document?.documentTypeId;
}

/**
 * Custom hook that returns a boolean <b>value</b> indicating whether the user is viewing a form inside a drawer or not.
 * @returns {bool} true then will show only form body; false otherwise.
 */
export function useHasBodyIdFilter() {
	const [searchParams] = useSearchParams();

	const hasBodyIdFilter = useSelector((state) => state.hasBodyIdFilter);

	if (Number(searchParams.get('bodyid')) > 0 || hasBodyIdFilter) {
		return true;
	}

	return false;
}

/**
 * Custom hook that returns <b>value</b> of the applied filter.
 * @returns {bool} true then will show only form body; false otherwise.
 */
export function useNavigationFilter() {
	const navigationFilter = useSelector((state) => state.navigationFilter);
	return navigationFilter;
}

/**
 * Custom hook that returns smartEvidenceSourceId related to a document.
 * @param {string} documentId - The document ID to look up.
 * @param {number} [targetEntityId] - Optional entity ID to filter by. If provided, returns undefined when the document's smartEvidenceSourceEntityId doesn't match.
 * @returns {number|undefined} smartEvidenceSourceId if exists and matches targetEntityId (when provided), otherwise undefined.
 */
export function useSmartEvidenceSourceId(documentId, targetEntityId = undefined) {
	const document = useSelector((state) => (documentId ? getTaskDocumentByDocumentId(state, documentId) : undefined));

	let smartEvidenceSourceId = document?.smartEvidenceSourceId;
	if (!isNullOrUndefined(targetEntityId) && document?.smartEvidenceSourceEntityId !== targetEntityId) {
		smartEvidenceSourceId = undefined;
	}

	return smartEvidenceSourceId;
}

/**
 * Custom hook that returns smartEvidenceSourceId related to a document.
 * @param {string} documentId documentId.
 * @returns {number} smartEvidenceSourceId if exists.
 */
export function useSmartEvidenceSourceEntityId(documentId) {
	let smartEvidenceSourceEntityId;

	const document = useSelector((state) => documentId && getTaskDocumentByDocumentId(state, documentId));

	//TODO : remove this when casing is fixed
	smartEvidenceSourceEntityId = document?.smartEvidenceSourceEntityId ?? document?.smartEvidenceSourceEntityID;

	return smartEvidenceSourceEntityId;
}

/**
 * Custom hook that returns the task document by document id.
 * @returns {object} canvasFormDocument
 */
export function useCanvasFormDocument() {
	const canvasFormDocument = useSelector((state) => getTaskDocumentByDocumentId(state, useDocumentId()));
	return canvasFormDocument;
}

/**
 * Custom hook that returns an account related to a document.
 * @param {number} documentId documentId.
 * @returns {object} account if exists.
 */
export function useSmartEvidenceSource(documentId) {
	let smartEvidenceSource;

	if (documentId) {
		const document = useSelector((state) => getTaskDocumentByDocumentId(state, documentId));
		smartEvidenceSource = useSelector((state) => getAccountById(state, document?.smartEvidenceSourceId));
	}

	return smartEvidenceSource;
}

export function useDecimalPattern(defaultSeparator = '.') {
	const userPreferences = useSelector((state) => state.userPreferences);

	return positiveNumberWithOneDecimalRegex(numberFormats.find((x) => x.id === userPreferences.numberFormat)?.decimalSeparator || defaultSeparator);
}

/**
 * Returns the selected stEntityId, if it exists in the store, otherwise it returns the one from the url parameters.
 * @returns {Array} selected stEntityId id.
 */
export function useSelectedStEntityId() {
	let selectedStEntityData = [];

	const {stEntityId} = useAresUrlParameters();
	const stEntityIdStore = useSelector((state) => state.stEntityId);

	if (!_.isEmpty(stEntityIdStore)) {
		selectedStEntityData = [stEntityIdStore.currentValue, stEntityIdStore.previousValue];
	} else if (stEntityId) {
		selectedStEntityData = [stEntityId];
	}

	return selectedStEntityData;
}

export function useCurrentTrialBalanceId() {
	const engagementHelixProjects = useSelector((state) => state.engagementHelixProjects);
	const isPrimary = engagementHelixProjects?.find((project) => project.isPrimary);
	if (isPrimary) {
		return isPrimary.trialBalanceId;
	}
	return null;
}

export function useCurrentTrialBalance() {
	const engagementHelixProjects = useSelector((state) => state.engagementHelixProjects);
	const primaryProject = engagementHelixProjects?.find((project) => project.isPrimary);
	if (primaryProject) {
		return primaryProject;
	}
	return null;
}

///Builds a promise using the available parameters.
///Engagement: the engagement.
///DocumentId: the document Id.
///QueryParams: query params string to apply to the built request url. Expands will be joined at the end.
///Payload: parameters to add to the get request as payload. Same level as meta object.
///Returns a promise to get form by id.
export function expandableGetFormById(engagement, documentId, queryParams, payload, expands = ['headers', 'headers.sections', 'guidance'], isAresView) {
	let _queryParams = queryParams ? queryParams + '&' : '';

	if (isAresView) {
		expands.push(...['embeddedDocumentBodies', 'mest']);
	}

	_queryParams += 'expand=' + expands.join(',');

	return getFormData(Utility.getUrlForFormData(engagement.engagementId, documentId, _queryParams), payload);
}

export function getMateriality(engagementId, stEntityId = undefined, trackWithArea = undefined) {
	let queryStringParams = {};

	if (stEntityId !== undefined) {
		queryStringParams.stEntityId = stEntityId;
	}

	return _getMateriality(engagementId, trackWithArea, !_.isEmpty(queryStringParams) ? queryStringParams : undefined);
}

export function getDocumentsUrl(engagementId, taskId) {
	let queryStringParameters = {};

	if (taskId) {
		queryStringParameters = {
			filteredEntityType: documentSearchTypes.task,
			filteredEntityIdGuid: taskId,
			documentCategoryName: documentCategoryNames.evidence,
			includeApprovals: true,
			pageSize: -1
		};
	} else {
		queryStringParameters = {
			documentCategoryName: documentCategoryNames.evidence,
			includeApprovals: true,
			pageSize: -1
		};
	}

	const searchString = !_.isEmpty(queryStringParameters) ? `?${queryString.stringify(queryStringParameters)}` : '';
	return `${Env.getBaseURL(serviceURL, engagementId)}Documents${searchString}`;
}

export function useCreateEntityForm(engagementId, entityId, documentTypeIdToCreate, taskId, objectIds, autoCreateAssociateDocuments = true) {
	return createEntityForm(engagementId, {entityId, documentTypeId: documentTypeIdToCreate, taskId, objectIds, autoCreateAssociateDocuments});
}

export function useNavRelevantDocumentsFilterer(customFilter) {
	const documents = useSelector((store) => getTaskDocumentsByIdSlice(store));

	return (documentIds) =>
		documentIds &&
		documentIds.filter((docId) => {
			const document = documents[docId];
			return docId && documentTypesShowedOnLeftNav.indexOf(document?.documentTypeId) > -1 && (!customFilter || (customFilter && customFilter(document)));
		});
}

export function useEngagementUserIsEngagementAccessible() {
	const userPreferences = useSelector((state) => state.userPreferences);
	if (isNullOrUndefined(userPreferences?.engagementUserIsEngagementAccessible)) {
		return true;
	}
	return userPreferences?.engagementUserIsEngagementAccessible;
}

export function useMestDocumentFilter() {
	const [selectedStEntityId] = useSelectedStEntityId();

	if (selectedStEntityId) {
		return (document) => document.entities === undefined || document.entities.length === 0 || document.entities.indexOf(selectedStEntityId) > -1;
	}

	return () => true;
}

export function useGetAllAresDocuments() {
	const engagement = useEngagement();
	const isMestEngagement = engagement?.isMultiEntityEngagement;
	const [selectedStEntityId] = useSelectedStEntityId();

	return useCallback(
		(engagementId, taskIdList, documentTypeIdList, documentCategoryName, payload, pageSize) => {
			if (isMestEngagement) {
				return getAllAresDocuments(engagementId, taskIdList, documentTypeIdList, documentCategoryName, payload, pageSize, isMestEngagement, selectedStEntityId);
			}

			return getAllAresDocuments(engagementId, taskIdList, documentTypeIdList, documentCategoryName, payload, pageSize);
		},
		[isMestEngagement, selectedStEntityId]
	);
}

export function useGetTheme() {
	const theme = useSelector((state) => state.theme);
	return theme;
}

export function useAccountProcedures(account) {
	const accountAssertions = account?.assertions
		? account.assertions.reduce((acc, assertion) => {
				acc[assertion.id] = assertion;
				return acc;
		  }, {})
		: {};

	//Limited risk accounts bring OSP tasks inside accountTasks when they do not have any risk associated.
	const allTasks = [...(account?.assertionTasks || []), ...(account?.accountTasks || [])];

	const procedures = allTasks
		? Object.values(
				allTasks
					.filter(
						(task) =>
							(task.taskTypeId === TaskTypeEnum.PRIMARY_SUBSTANTIVE_PROCEDURE_TASK || task.taskTypeId === TaskTypeEnum.OTHER_SUBSTANTIVE_PROCEDURE_TASK) &&
							task.isRejected === false
					)
					.reduce((acc, task) => {
						if (!acc[task.taskId]) {
							acc[task.taskId] = {
								...task,
								assertions: accountAssertions[task.assertionID] ? [accountAssertions[task.assertionID]] : []
							};
						} else {
							if (!acc[task.taskId].assertions.some((a) => a.id === task.assertionID)) {
								if (accountAssertions[task.assertionID]) {
									acc[task.taskId].assertions.push(accountAssertions[task.assertionID]);
								}
							}
						}
						return acc;
					}, {})
		  )
		: [];

	// Order first by taskTypeId descending (17 first, then 16), then by taskDisplayName for type 16
	return procedures.sort((a, b) => {
		if (b.taskTypeId !== a.taskTypeId) {
			return b.taskTypeId - a.taskTypeId; // Descending order: 17 before 16
		}
		if (a.taskTypeId === TaskTypeEnum.OTHER_SUBSTANTIVE_PROCEDURE_TASK && b.taskTypeId === TaskTypeEnum.OTHER_SUBSTANTIVE_PROCEDURE_TASK) {
			return (a.taskDisplayName || '').localeCompare(b.taskDisplayName || '', undefined, {sensitivity: 'base'});
		}
		return 0;
	});
}

// Extract and process ROMMs from account assertions
export function useAccountROMMs(account) {
	if (!account?.assertions || !Array.isArray(account.assertions)) return [];

	// Collect all risks from all assertions
	const allRisks = [];
	account.assertions.forEach((assertion) => {
		if (assertion.risks && Array.isArray(assertion.risks)) {
			assertion.risks.forEach((risk) => {
				// Filter out rejected risks
				if (RiskFilters.SignificantRisk(risk) //type 1
					|| RiskFilters.FraudRisk(risk) //type 2
					|| RiskFilters.AnyInherentRisk(risk)) { // type 3, regardless of subtype
					allRisks.push({
						...risk,
						assertion
					});
				}
			});
		}
	});

	// Group risks by risk ID to avoid duplicates
	const riskMap = new Map();
	allRisks.forEach((risk) => {
		if (!riskMap.has(risk.id)) {
			riskMap.set(risk.id, {
				...risk,
				assertions: [risk.assertion]
			});
		} else {
			const existing = riskMap.get(risk.id);
			if (!existing.assertions.find((a) => a.id === risk.assertion.id)) {
				existing.assertions.push(risk.assertion);
			}
		}
	});

	const uniqueRisks = sortRisks(Array.from(riskMap.values()));

	return uniqueRisks;
}

/**
 * Returns the parentSmartEvidenceSourceId.
 * @returns {String} The current Document ID.
 */
export function useParentSmartEvidenceSourceId() {
	let aresTaskDocuments = useSelector((state) => state?.aresTaskDocuments);
	let parentSmartEvidenceSourceId = Object.values(aresTaskDocuments.documents.byId)[0]?.parentSmartEvidenceSourceId;
	return parentSmartEvidenceSourceId;
}

/**
 * Returns whether the parent document is signed off.
 * @returns {Boolean} True if the parent document is signed off, false otherwise.
 */
export function useIsParentDocumentSignOff() {
	let aresTaskDocuments = useSelector((state) => state?.aresTaskDocuments);
	let isParentDocumentSignOff = Object.values(aresTaskDocuments.documents.byId)[0]?.isParentDocumentSignOff ?? true;
	return isParentDocumentSignOff;
}

/**
 * Custom hook that returns the base Helix analyzer URL without the project ID path.
 * @returns {String} The base analyzer URL (e.g., https://example.com) or undefined if not available.
 */
export function useHelixAnalyzerBaseUrl(projectId) {
	const helixProjects = useSelector((store) => store.helixProjects);
	const project = projectId ? helixProjects?.find((p) => p.projectId === projectId) : helixProjects?.[0];

	// Get the first project from the array
	const analyzerUrl = project?.helixProjectAttributesReadModel?.analyzerUrl;
	const audienceUri = project?.helixProjectAttributesReadModel?.helixAudience;

	if (!audienceUri || !analyzerUrl) {
		return undefined;
	}

	return {
		analyzerUrl,
		audienceUri
	};
}

export function usePageRefresh() {
	const [isMFE, , mfeInstanceId] = Utility.getMFEAttributes();
	const isDrawerMode = useIsDrawerMode();
	const {frameKey: activeFrameKey} = useActiveFrame();
	const navigate = useNavigate();
	const location = useLocation();

	return () => {
		if (isMFE && !isNullOrUndefined(mfeInstanceId)) {
			// In MFE mode using remote federated component with a valid instance id.
			const eventName = `${clientSideEventNames.refreshMFE}_${mfeInstanceId}`;
			window.dispatchEvent(new CustomEvent(eventName));
			return;
		}

		// Old drawer implementation or MFE without a valid instance id.
		if (isDrawerMode || activeFrameKey || document.body?.getAttribute('frame-key')) {
			// Navigate with a unique refreshKey state to force the Outlet to remount its subtree,
			// re-triggering all initial data fetches without relying on specific API events.
			navigate(location.pathname + location.search, {replace: true, state: {refreshKey: Date.now()}});
			return;
		}

		// Ares standalone, full view - No MFE/Drawer, so we can do a full page reload.
		// Or no key was found to update the frame, so fallback to full page reload.
		window.location.reload();
	};
}

export function useAccTypeIdLoader() {
	const docTypeId = useDocumentTypeId();
	const docTypeIdRef = useRef(docTypeId);
	const refreshPage = usePageRefresh();
	const refreshPageRef = useRef(refreshPage);
	refreshPageRef.current = refreshPage;

	useEffect(() => {
		docTypeIdRef.current = docTypeId;
	}, [docTypeId]);

	return useCallback(() => {
		if (docTypeIdRef.current === documentTypes.ALRA_INDIVIDUAL) {
			const delay = 30;

			showBlockingLoader({
				seconds: delay,
				message: labels.contentDeliveryInProgress.replaceAll('{0}', delay),
				callback: () => refreshPageRef.current()
			});
		}
	}, []);
}
